import type { FaqBlock } from './faqTypes';

type FaqAnswerProps = {
  blocks: FaqBlock[];
  /**
   * [MCV-1592] Heading level for `h` blocks. Defaults to 4, the level it has
   * always emitted. Grouped sections sit one level deeper, so their answers'
   * sub-headings must follow or they become peers of the subgroup heading
   * above them.
   */
  headingLevel?: 3 | 4 | 5;
};

function renderBlock(block: FaqBlock, index: number, headingLevel: 3 | 4 | 5) {
  switch (block.kind) {
    case 'h': {
      const Heading = `h${headingLevel}` as const;
      return (
        <Heading key={index} className="pt-1 text-[0.95rem] font-semibold leading-6 text-grey-100">
          {block.text}
        </Heading>
      );
    }

    case 'ul':
      return (
        <ul key={index} className="list-disc space-y-1.5 pl-5 marker:text-primary-50">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex} className="text-[1rem] leading-7 text-grey-300">
              {item}
            </li>
          ))}
        </ul>
      );

    case 'ol':
      return (
        <ol key={index} className="list-decimal space-y-1.5 pl-5 marker:text-primary-50">
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex} className="text-[1rem] leading-7 text-grey-300">
              {item}
            </li>
          ))}
        </ol>
      );

    case 'table':
      return (
        <div key={index} className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            {block.headers.length > 0 && (
              <thead>
                <tr>
                  {block.headers.map((header, headerIndex) => (
                    <th
                      key={headerIndex}
                      className="border-b border-grey-700 px-3 py-2 font-semibold text-grey-100"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="align-top">
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border-b border-grey-800 px-3 py-2 text-grey-300"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'p':
      return (
        <p
          key={index}
          className={
            block.emphasis
              ? 'text-[1rem] font-semibold leading-7 text-grey-100'
              : 'text-[1rem] leading-7 text-grey-300'
          }
        >
          {block.text}
        </p>
      );

    case 'contact':
      return (
        <p key={index} className="text-[1rem] leading-7 text-grey-300">
          {block.text ? `${block.text} ` : null}
          {block.email && (
            <a
              href={`mailto:${block.email}`}
              className="font-medium text-primary-50 underline hover:no-underline"
            >
              {block.email}
            </a>
          )}
          {block.email && block.phone ? ' or ' : null}
          {block.phone && (
            <a
              href={`tel:${block.phone.replace(/[^\d+]/g, '')}`}
              className="font-medium text-primary-50 underline hover:no-underline"
            >
              {block.phone}
            </a>
          )}
        </p>
      );

    default: {
      // Exhaustiveness guard: adding a new FaqBlock kind without handling it here
      // is a compile-time error.
      const exhaustive: never = block;
      void exhaustive;
      return null;
    }
  }
}

// Renders an answer made up of paragraph / list / sub-heading / table blocks.
export const FaqAnswer = ({ blocks, headingLevel = 4 }: FaqAnswerProps) => {
  return (
    <div className="space-y-3">
      {blocks.map((block, index) => renderBlock(block, index, headingLevel))}
    </div>
  );
};
